<template>
  <Modal
    name="CREATE_EDIT_SPLIT_CONFIGURATION_MODAL"
    :title="title"
    :width="820"
    actions
  >
    <template v-slot:body>
      <v-form ref="splitConfigurationForm" v-model="valid" lazy-validation>
        <v-select
          v-model="vendor"
          :label="$t('split.label.vendor')"
          :items="vendors"
          :rules="rules.required"
          item-text="name"
          item-value="id"
          return-object
          single-line
          outlined
        />

        <v-textarea
          v-model="description"
          :label="$t('split.label.description')"
          :height="100"
          outlined
        />

        <v-select
          v-model="partner"
          :label="$t('split.label.partner')"
          :items="partners"
          item-text="name"
          item-value="id"
          return-object
          single-line
          clearable
          outlined
        />

        <h2 class="mt-4 mb-2">{{ $t('split.label.taxes') }}</h2>

        <v-row>
          <v-col cols="12" sm="6">
            <v-card class="pa-3" outlined>
              <h4 class="mb-2">{{ $t('split.label.host') }}</h4>
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="host_take_rate"
                    v-money="host_take_rate_mode ? percentage : catalogCurrency()"
                    :error="host_take_rate_validate"
                    :label="$t('split.label.tax')"
                    class="amount inputAmount"
                    hide-details="auto"
                    outlined
                  />
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="host_take_rate_mode"
                    :label="$t('split.label.mode')"
                    :items="mode_select"
                    hide-details="auto"
                    outlined
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6">
            <v-card class="pa-3" outlined>
              <h4 class="mb-2">{{ $t('split.label.customer') }}</h4>
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="customer_take_rate"
                    v-money="customer_take_rate_mode ? percentage : catalogCurrency()"
                    :error="customer_take_rate_validate"
                    :label="$t('split.label.tax')"
                    class="amount inputAmount"
                    hide-details="auto"
                    outlined
                  />
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="customer_take_rate_mode"
                    :label="$t('split.label.mode')"
                    :items="mode_select"
                    hide-details="auto"
                    outlined
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <v-row justify="center">
          <v-col cols="12" sm="6">
            <v-card class="pa-3" outlined>
              <h4 class="mb-2">{{ $t('split.label.hauseful') }}</h4>
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="hauseful_take_rate"
                    v-money="hauseful_take_rate_mode ? percentage : catalogCurrency()"
                    :error="hauseful_take_rate_validate"
                    :label="$t('split.label.tax')"
                    class="amount inputAmount"
                    hide-details="auto"
                    outlined
                  />
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="hauseful_take_rate_mode"
                    :label="$t('split.label.mode')"
                    :items="mode_select"
                    hide-details="auto"
                    outlined
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>
          <v-col v-if="partner" cols="12" sm="6">
            <v-card class="pa-3" outlined>
              <h4 class="mb-2">{{ $t('split.label.partner') }}</h4>
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="partner_take_rate"
                    v-money="partner_take_rate_mode ? percentage : catalogCurrency()"
                    :error="partner_take_rate_validate"
                    :label="$t('split.label.tax')"
                    class="amount inputAmount"
                    hide-details="auto"
                    outlined
                  />
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="partner_take_rate_mode"
                    :label="$t('split.label.mode')"
                    :items="mode_select"
                    hide-details="auto"
                    outlined
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </template>
    <template v-slot:actions>
      <v-btn
        @click="closeAllModals"
        :disabled="isSplitConfigurationLoading"
        elevation="0"
        class="mr-5"
        large
        text
      >
        {{ $t('item.action.cancel') }}
      </v-btn>

      <!-- create item -->
      <v-btn
        @click="createSplitConfiguration"
        :loading="isSplitConfigurationLoading"
        color="primary"
        elevation="0"
        large
      >
        {{
          splitConfiguration
            ? $t('split.action.edit')
            : $t('split.action.create')
        }}
      </v-btn>
      <!-- /create item -->
    </template>
  </Modal>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex'
import { required } from '@/util/defaultValidationRules'
import { clearCurrency } from '../../../util/helpers'
import { catalogCurrency } from '../../../util/catalog'

import Modal from '@/components/app/Modal'
import HAmount from '@/components/app/HAmount'
import HCheckbox from '@/components/app/HCheckbox'

import { VMoney } from 'v-money'

export default {
  name: 'SplitConfigurationCreateEdit',

  components: {
    Modal,
    HAmount,
    HCheckbox,
  },

  directives: {
    money: VMoney
  },

  data() {
    return {
      vendor: null,
      description: null,

      host_take_rate: 0,
      host_take_rate_mode: false,
      host_take_rate_validate: false,

      customer_take_rate: 0,
      customer_take_rate_mode: false,
      customer_take_rate_validate: false,

      hauseful_take_rate: 0,
      hauseful_take_rate_mode: false,
      hauseful_take_rate_validate: false,

      partner_take_rate: 0,
      partner_take_rate_mode: false,
      partner_take_rate_validate: false,

      results: [],

      valid: false,
      search: null,
      isLoading: false,
      _timerId: null,

      rules: {
        required,
      },

      mode_select: [
        {
          text: this.$t('split.label.fixAmount'),
          value: false,
        },
        {
          text: this.$t('split.label.percentage'),
          value: true,
        },
      ],

      partner: null,

      catalogCurrency,

      percentage: {
        prefix: '',
        thousands: '',
        decimal: '.',
        prefix: '',
        suffix: '%',
        precision: 2,
      },
    }
  },

  watch: {
    search(term) {
      clearTimeout(this._timerId)

      this._timerId = setTimeout(() => {
        if (!term) return
        this.searchVendors(term)
      }, 1000)
    },
  },

  computed: {
    ...mapGetters([
      'isSplitConfigurationLoading',
      'vendorSearchResults',
      'splitConfiguration',
      'vendors',
      'partners',
    ]),

    title() {
      return this.isSplitConfigurationLoading
        ? this.$t('split.action.loading')
        : this.item
        ? this.$t('split.action.edit')
        : this.$t('split.action.create')
    },
  },

  methods: {
    ...mapMutations(['closeAllModals']),
    ...mapActions([
      'fetchVendors',
      'searchVendors',
      'createEditSplitConfiguration',
      'fetchAllPartners',
    ]),

    createSplitConfiguration() {
      this.validateValue(this.host_take_rate, 'host_take_rate')
      this.validateValue(this.customer_take_rate, 'customer_take_rate')
      this.validateValue(this.hauseful_take_rate, 'hauseful_take_rate')
      this.validateValue(this.partner_take_rate, 'partner_take_rate')

      if (!this.$refs.splitConfigurationForm.validate()) return

      const splitConfiguration = {
        vendor_id: this.vendor?.id,
        description: this.description,

        host_take_rate: this.clearValue(this.host_take_rate),
        customer_take_rate: this.clearValue(this.customer_take_rate),
        hauseful_take_rate: this.clearValue(this.hauseful_take_rate),

        host_take_rate_mode: this.host_take_rate_mode,
        customer_take_rate_mode: this.customer_take_rate_mode,
        hauseful_take_rate_mode: this.hauseful_take_rate_mode,

        partner_take_rate: this.partner ? this.clearValue(this.partner_take_rate) : null,
        partner_take_rate_mode: this.partner
          ? this.partner_take_rate_mode
          : null,
        partner_id: this.partner ? this.partner.id : null,
      }

      this.createEditSplitConfiguration(splitConfiguration)
    },

    resetForm() {
      this.vendor = null
      this.description = null

      this.host_take_rate = 0
      this.host_take_rate_mode = false
      this.host_take_rate_validate = false

      this.customer_take_rate = 0
      this.customer_take_rate_mode = false
      this.customer_take_rate_validate = false

      this.hauseful_take_rate = 0
      this.hauseful_take_rate_mode = false
      this.hauseful_take_rate_validate = false

      this.partner_take_rate = 0
      this.partner_take_rate_mode = false
      this.partner_take_rate_validate = false

      this.partner = null

      this.$refs?.splitConfigurationForm?.reset()
    },

    clearValue(value) {
      if (!value) return
      if (value.includes('R$') || value.includes('A$')) return clearCurrency(value)
      return parseFloat(value.replace('% ','')).toFixed(2)
    },

    validateValue(value, type) {
      if (!value) return

      const clearedValue = this.clearValue(value)

      if (this[`${type}_mode`]) {
        // percentage
        if (clearedValue > '100.00') {
          this[`${type}_validate`] = true
          return true
        }
      } else {
        // currency
        if (clearedValue > '10000.00') {
          this[`${type}_validate`] = true
          return true
        }
      }

      this[`${type}_validate`] = false
      return false
    }
  },

  mounted() {
    this.fetchVendors()
    this.fetchAllPartners()
  },
}
</script>
